// Sistem yönetimi API istemcisi.
//
// Sunucu sözleşmesi: services/api/Btys.Api/Modules/Sistem/Dto.cs
// Tüm uçlar SistemIzni() kapısından geçiyor — tenant rolüyle 403 döner.

import { api } from '../../lib/api'

export type TenantDurumu = 'aktif' | 'askida' | 'pasif'

export type TesisSatiri = {
  id: string
  tenantId: string
  kod: string
  ad: string
  il: string | null
  aktif: boolean
  kullaniciSayisi: number
}

export type TenantSatiri = {
  id: string
  kod: string | null
  unvan: string
  vergiNo: string | null
  durum: TenantDurumu
  durumZamani: string | null
  durumGerekce: string | null
  olusturmaZamani: string
  tesisSayisi: number
  aktifTesisSayisi: number
  kullaniciSayisi: number
  aktifKullaniciSayisi: number
  sonGiris: string | null
  tesisler: TesisSatiri[]
}

export type KopyaOzeti = {
  uniteSinifi: number
  stokKarti: number
  esik: number
  atlanan: number
}

export type TenantOlusturIstek = {
  unvan: string
  kod: string | null
  vergiNo: string | null
  tesisKod: string
  tesisAd: string
  tesisIl: string | null
  yoneticiEposta: string
  yoneticiAd: string
  yoneticiSifre: string
  kutuphaneKopyala: boolean
}

export type TenantOlusturYanit = {
  tenant: TenantSatiri
  yoneticiId: string
  kopya: KopyaOzeti | null
}

// --------------------------------------------------------------- kütüphaneler

export type UniteGrubu = 'on' | 'cur' | 'depo' | 'gaz' | 'enerji'
export type StokTuru = 'hammadde' | 'urun' | 'sarf' | 'yedek' | 'yakit'
export type VeriGuveni = 'olcum' | 'kurum' | 'celisik'

export type SistemUniteSinifi = {
  id: string
  kodPrefix: string
  ad: string
  grup: UniteGrubu
  aciklama: string | null
  tSablon: string
  bSablon: string
  sira: number
  aktif: boolean
}

export type SistemStokKarti = {
  id: string
  kod: string
  ad: string
  tur: StokTuru
  grup: string | null
  birim: string
  hij: boolean
  km: number | null
  okmKm: number | null
  verimNlKgOkm: number | null
  ch4Oran: number | null
  metanVerimiBmp: number | null
  cNOrani: number | null
  yogunluk: number | null
  veriKaynagi: string | null
  veriGuven: VeriGuveni | null
  sira: number
  aktif: boolean
}

export type SistemEsik = {
  id: string
  gosterge: string
  normalMin: number | null
  normalMax: number | null
  amber: number | null
  kirmizi: number | null
  sira: number
  aktif: boolean
}

export type SistemStokTuru = {
  id: string
  tur: StokTuru
  etiket: string
  aciklama: string | null
  kodOneki: string
  sira: number
  aktif: boolean
}

export type SistemBirim = {
  id: string
  kod: string
  ad: string
  tur: StokTuru | null
  sira: number
  aktif: boolean
}

const kok = '/api/sistem'
const kut = `${kok}/kutuphane`

export const sistem = {
  tenantlar: () => api<TenantSatiri[]>(`${kok}/tenantlar`),

  tenantOlustur: (govde: TenantOlusturIstek) =>
    api<TenantOlusturYanit>(`${kok}/tenantlar`, { yontem: 'POST', govde }),

  tenantGuncelle: (id: string, govde: { unvan: string; kod: string | null; vergiNo: string | null }) =>
    api<TenantSatiri>(`${kok}/tenantlar/${id}`, { yontem: 'PUT', govde }),

  /** Gerekçe zorunlu — sunucu boş gerekçede 409 döner. */
  askiyaAl: (id: string, gerekce: string) =>
    api<TenantSatiri>(`${kok}/tenantlar/${id}/askiya-al`, { yontem: 'POST', govde: { gerekce } }),

  pasifeAl: (id: string, gerekce: string) =>
    api<TenantSatiri>(`${kok}/tenantlar/${id}/pasife-al`, { yontem: 'POST', govde: { gerekce } }),

  etkinlestir: (id: string) =>
    api<TenantSatiri>(`${kok}/tenantlar/${id}/etkinlestir`, { yontem: 'POST' }),

  /** Kütüphaneyi tenant'ın tüm tesislerine TAMAMLAR (üzerine yazmaz). */
  senkronize: (id: string) =>
    api<KopyaOzeti>(`${kok}/tenantlar/${id}/senkronize`, { yontem: 'POST' }),

  tesisOlustur: (
    tenantId: string,
    govde: { kod: string; ad: string; il: string | null; kutuphaneKopyala: boolean },
  ) => api<TesisSatiri>(`${kok}/tenantlar/${tenantId}/tesisler`, { yontem: 'POST', govde }),

  tesisGuncelle: (
    id: string,
    govde: { kod: string; ad: string; il: string | null; aktif: boolean },
  ) => api<TesisSatiri>(`${kok}/tesisler/${id}`, { yontem: 'PUT', govde }),

  // --- kütüphaneler ---
  uniteSiniflari: () => api<SistemUniteSinifi[]>(`${kut}/unite-siniflari`),
  uniteSinifiYaz: (id: string | null, govde: Omit<SistemUniteSinifi, 'id'>) =>
    id
      ? api<SistemUniteSinifi>(`${kut}/unite-siniflari/${id}`, { yontem: 'PUT', govde })
      : api<SistemUniteSinifi>(`${kut}/unite-siniflari`, { yontem: 'POST', govde }),
  uniteSinifiSil: (id: string) =>
    api<void>(`${kut}/unite-siniflari/${id}`, { yontem: 'DELETE' }),

  stokKartlari: () => api<SistemStokKarti[]>(`${kut}/stok-kartlari`),
  stokKartiYaz: (id: string | null, govde: Omit<SistemStokKarti, 'id'>) =>
    id
      ? api<SistemStokKarti>(`${kut}/stok-kartlari/${id}`, { yontem: 'PUT', govde })
      : api<SistemStokKarti>(`${kut}/stok-kartlari`, { yontem: 'POST', govde }),
  stokKartiSil: (id: string) => api<void>(`${kut}/stok-kartlari/${id}`, { yontem: 'DELETE' }),

  esikler: () => api<SistemEsik[]>(`${kut}/esikler`),
  esikYaz: (id: string | null, govde: Omit<SistemEsik, 'id'>) =>
    id
      ? api<SistemEsik>(`${kut}/esikler/${id}`, { yontem: 'PUT', govde })
      : api<SistemEsik>(`${kut}/esikler`, { yontem: 'POST', govde }),
  esikSil: (id: string) => api<void>(`${kut}/esikler/${id}`, { yontem: 'DELETE' }),

  stokTurleri: () => api<SistemStokTuru[]>(`${kut}/stok-turleri`),
  /** Tür KODU değişmez — yalnız etiket, önek, sıra ve görünürlük. */
  stokTuruYaz: (
    id: string,
    govde: { etiket: string; aciklama: string | null; kodOneki: string; sira: number; aktif: boolean },
  ) => api<SistemStokTuru>(`${kut}/stok-turleri/${id}`, { yontem: 'PUT', govde }),

  birimler: () => api<SistemBirim[]>(`${kut}/birimler`),
  birimYaz: (id: string | null, govde: Omit<SistemBirim, 'id'>) =>
    id
      ? api<SistemBirim>(`${kut}/birimler/${id}`, { yontem: 'PUT', govde })
      : api<SistemBirim>(`${kut}/birimler`, { yontem: 'POST', govde }),
  birimSil: (id: string) => api<void>(`${kut}/birimler/${id}`, { yontem: 'DELETE' }),
}
